import { useMemo } from 'react';
import type { Appointment } from '../../types/appointments';
import { ApprovalQueueCard, lateRiskSortScore } from './ApprovalQueueCard';

// ─────────────────────────────────────────────────────
//  ApprovalQueueList — shared approval queue container
//  Renders the amber "Awaiting Approval" section with
//  priority-sorted cards.
//  Used by: Dashboard, Schedule (AppointmentsTab)
// ─────────────────────────────────────────────────────

export interface ApprovalQueueListProps {
    /** Unsorted pending requests — sorting is handled internally */
    requests: Appointment[];
    /** Fired when the card body is clicked (usually opens detail) */
    onCardClick: (request: Appointment) => void;
    /** Approve action */
    onApprove: (request: Appointment) => void;
    /** Revise Time action */
    onRevise: (request: Appointment) => void;
    /** Decline action */
    onDecline: (request: Appointment) => void;
    /** Optional element id for anchor/scroll targeting */
    id?: string;
    /** Warehouse timezone */
    warehouseTimeZone?: string;
}

// ── Sort helpers (inlined to avoid cross-file dependency) ──

function toTimeMsSafe(iso?: string | null): number | null {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return isNaN(t) ? null : t;
}

function riskScore(level?: string): number {
    if (level === 'High') return 3;
    if (level === 'Medium') return 2;
    if (level === 'Low') return 1;
    return 0;
}

/** Priority sort: Late PO Risk (critical→urgent→standard) → Delay Risk (high→low) → Schedule time (earliest) → Request age (newest) */
function sortPendingRequests(requests: Appointment[]): Appointment[] {
    return [...requests].sort((a, b) => {
        // V1.3A: Late PO risk is the highest priority sort key
        const lrA = lateRiskSortScore(a);
        const lrB = lateRiskSortScore(b);
        if (lrA !== lrB) return lrB - lrA;

        const rA = riskScore(a.delayRiskLevel);
        const rB = riskScore(b.delayRiskLevel);
        if (rA !== rB) return rB - rA;

        const tA = toTimeMsSafe(a.startAt);
        const tB = toTimeMsSafe(b.startAt);
        if (tA !== null && tB !== null && tA !== tB) return tA - tB;
        if (tA !== null && tB === null) return -1;
        if (tA === null && tB !== null) return 1;

        const reqA = toTimeMsSafe(a.requestedAt) || 0;
        const reqB = toTimeMsSafe(b.requestedAt) || 0;
        return reqB - reqA;
    });
}

export function ApprovalQueueList({
    requests,
    onCardClick,
    onApprove,
    onRevise,
    onDecline,
    id,
    warehouseTimeZone,
}: ApprovalQueueListProps) {
    const sorted = useMemo(() => sortPendingRequests(requests), [requests]);

    if (sorted.length === 0) return null;

    return (
        <div
            id={id}
            className="rounded-xl border-2 border-amber-300 dark:border-amber-700/60 bg-amber-50/40 dark:bg-amber-950/20 overflow-hidden"
        >
            <div className="px-4 py-3 border-b border-amber-200 dark:border-amber-800/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                        Awaiting Approval
                    </span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-800/60 text-amber-800 dark:text-amber-300 tabular-nums">
                        {sorted.length}
                    </span>
                </div>
            </div>
            <div className="p-3 space-y-4">
                {sorted.map((request) => (
                    <ApprovalQueueCard
                        key={request.id}
                        request={request}
                        warehouseTimeZone={warehouseTimeZone}
                        onCardClick={() => onCardClick(request)}
                        onApprove={() => onApprove(request)}
                        onRevise={() => onRevise(request)}
                        onDecline={() => onDecline(request)}
                    />
                ))}
            </div>
        </div>
    );
}
